import React, { useEffect, useRef, useState } from "react";

import { HubAccessLevel } from "@gamedev-web/one-gaming-crm";
import { BasicButton, ButtonType, Icon, IconSize } from "@gaming/xds-react-js";
import { Close, Delete, InfoSolid } from "@gaming/xds-react-js-icons";

import { HubNotification } from "../../../../components/HubNotification";
import { LoadingSpinner } from "../../../../components/Spinner";
import { useStrings } from "../../../../localization/localizationHelpers";
import type { AdminInvitationInput } from "../../../../models/adminPage";
import { getHubAccessPermissionLabels } from "../../../../models/hubPermissions";
import { InvitationRole } from "./InvitationRole";
import * as styles from "./InviteContactModal.module.css";

type InvitationFormProps = Readonly<{
  children?: React.ReactNode;
  resetKey: string | boolean;
  initialInvitation: AdminInvitationInput;
  title: React.ReactNode;
  titleId?: string;
  description: string;
  metadata?: React.ReactNode;
  submitLabel: string;
  cancelLabel: string;
  secondaryActionLabel?: string;
  closeLabel?: string;
  isDisabled?: boolean;
  isEmailReadOnly?: boolean;
  areContactFieldsReadOnly?: boolean;
  roleMode?: "select" | "assigned" | "hidden";
  roleContent?: React.ReactNode;
  allowInternalScroll?: boolean;
  hideOrganizationEmailNotice?: boolean;
  hideSubmit?: boolean;
  hideCancel?: boolean;
  dangerActionLabel?: string;
  assignedRoleActionLabel?: string;
  onCancel: () => void;
  onDangerAction?: () => void;
  onSecondaryAction?: () => void;
  onSubmit: (invitation: AdminInvitationInput) => Promise<void>;
}>;

export const InvitationForm = ({
  children,
  resetKey,
  initialInvitation,
  title,
  titleId,
  description,
  metadata,
  submitLabel,
  cancelLabel,
  secondaryActionLabel,
  closeLabel,
  isDisabled = false,
  isEmailReadOnly = false,
  areContactFieldsReadOnly = false,
  roleMode = "select",
  roleContent,
  allowInternalScroll = true,
  hideOrganizationEmailNotice = false,
  hideSubmit = false,
  hideCancel = false,
  dangerActionLabel,
  assignedRoleActionLabel,
  onCancel,
  onDangerAction,
  onSecondaryAction,
  onSubmit,
}: InvitationFormProps) => {
  const [firstName, setFirstName] = useState(initialInvitation.firstName);
  const [lastName, setLastName] = useState(initialInvitation.lastName);
  const [jobTitle, setJobTitle] = useState(initialInvitation.jobTitle);
  const [email, setEmail] = useState(initialInvitation.email);
  const [role, setRole] = useState<HubAccessLevel>(initialInvitation.accessLevel);
  const [error, setError] = useState<string | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [
    firstNameLabel,
    lastNameLabel,
    jobTitleOptionalLabel,
    emailLabel,
    accessLevelLabel,
    gameConceptAdminLabel,
    gameManagerLabel,
    submittedConceptsOnlyLabel,
    requiredFieldsError,
    submitError,
    contactInformationTitle,
    selectRoleTitle,
    assignedRoleTitle,
    hubAccessLabel,
    manageAllSubmissionsLabel,
    manageOwnSubmissionsLabel,
    manageCompanyDetailsLabel,
    manageHubSettingsLabel,
    organizationEmailNoticeTitle,
    organizationEmailNoticeDescription,
    submittingLabel,
    sendErrorTitle,
    tryAgainLabel,
    closeNotificationLabel,
  ] = useStrings(
    "partnerAdminFirstNameLabel",
    "partnerAdminLastNameLabel",
    "partnerAdminInviteJobTitleOptionalLabel",
    "partnerAdminEmailLabel",
    "partnerAdminAccessLevelLabel",
    "partnerAdminGameConceptAdminAccessLabel",
    "partnerAdminGameManagerAccessLabel",
    "partnerAdminSubmittedConceptsOnlyAccessLabel",
    "partnerAdminInviteRequiredFieldsError",
    "partnerAdminInviteSendError",
    "partnerAdminInviteContactInformationTitle",
    "partnerAdminInviteSelectRoleTitle",
    "partnerInvitationsAssignedRoleTitle",
    "partnerAdminInviteHubAccessLabel",
    "partnerAdminInviteManageAllSubmissionsLabel",
    "partnerAdminInviteManageOwnSubmissionsLabel",
    "partnerAdminInviteManageCompanyDetailsLabel",
    "partnerAdminInviteManageHubSettingsLabel",
    "partnerAdminInviteOrganizationEmailNoticeTitle",
    "partnerAdminInviteOrganizationEmailNoticeDescription",
    "partnerAdminInviteSendingLabel",
    "partnerAdminInviteSendErrorTitle",
    "partnerAdminTryAgainLabel",
    "partnerAdminNotificationCloseLabel"
  );
  const formRef = useRef<HTMLFormElement>(null);
  const roleOptions = (
    [
      {
        id: String(HubAccessLevel.Admin),
        label: gameConceptAdminLabel,
        accessLevel: HubAccessLevel.Admin,
      },
      {
        id: String(HubAccessLevel.GameManager),
        label: gameManagerLabel,
        accessLevel: HubAccessLevel.GameManager,
      },
      {
        id: String(HubAccessLevel.User),
        label: submittedConceptsOnlyLabel,
        accessLevel: HubAccessLevel.User,
      },
    ] as const
  ).map(option => ({
    ...option,
    permissionLabels: getHubAccessPermissionLabels(option.accessLevel, {
      manageAllSubmissions: manageAllSubmissionsLabel,
      manageOwnSubmissions: manageOwnSubmissionsLabel,
      manageCompanyDetails: manageCompanyDetailsLabel,
      manageHubSettings: manageHubSettingsLabel,
    }),
  }));
  const visibleRoles =
    roleMode === "assigned"
      ? roleOptions.filter(option => option.accessLevel === role)
      : roleOptions;
  const isBusy = isDisabled || isSubmitting;
  const isAADOrganizationEmail = email.trim().toLowerCase().endsWith(".onmicrosoft.com");

  useEffect(() => {
    setFirstName(initialInvitation.firstName);
    setLastName(initialInvitation.lastName);
    setJobTitle(initialInvitation.jobTitle);
    setEmail(initialInvitation.email);
    setRole(initialInvitation.accessLevel);
    setError(null);
  }, [initialInvitation, resetKey]);

  const submit = async (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    const normalizedEmail = email.trim().toLowerCase();
    if (!firstName.trim() || !lastName.trim() || !normalizedEmail) {
      setError(requiredFieldsError);
      return;
    }
    setError(null);
    setIsSubmitting(true);
    try {
      await onSubmit({
        firstName: firstName.trim(),
        lastName: lastName.trim(),
        jobTitle: jobTitle.trim(),
        email: normalizedEmail,
        accessLevel: role,
      });
    } catch {
      setError(submitError);
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <form
      ref={formRef}
      className={styles.form}
      data-internal-scroll={allowInternalScroll}
      aria-busy={isBusy}
      onSubmit={submit}
    >
      <div className={styles.header}>
        <div>
          <h2 id={titleId} className={styles.title}>
            {title}
          </h2>
          <p className={styles.description}>{description}</p>
          {metadata}
        </div>
        {closeLabel && (
          <button
            type="button"
            className={styles.close}
            aria-label={closeLabel}
            disabled={isBusy}
            onClick={onCancel}
          >
            <Icon aria-hidden="true" icon={Close} iconSize={IconSize.Custom} />
          </button>
        )}
      </div>
      <div className={styles.scrollContent}>
        <section className={styles.section}>
          <h3 className={styles.sectionTitle}>{contactInformationTitle}</h3>
          <div className={styles.fields}>
            <label className={styles.field}>
              <span>{firstNameLabel}</span>
              <input
                required
                disabled={isBusy || areContactFieldsReadOnly}
                maxLength={50}
                type="text"
                value={firstName}
                onChange={event => {
                  setFirstName(event.target.value);
                  setError(null);
                }}
              />
            </label>
            <label className={styles.field}>
              <span>{lastNameLabel}</span>
              <input
                required
                disabled={isBusy || areContactFieldsReadOnly}
                maxLength={50}
                type="text"
                value={lastName}
                onChange={event => {
                  setLastName(event.target.value);
                  setError(null);
                }}
              />
            </label>
            <label className={styles.field}>
              <span>{emailLabel}</span>
              <input
                required
                disabled={isBusy || areContactFieldsReadOnly}
                readOnly={isEmailReadOnly || areContactFieldsReadOnly}
                maxLength={254}
                type="email"
                value={email}
                onChange={event => {
                  setEmail(event.target.value);
                  setError(null);
                }}
              />
            </label>
            {!hideOrganizationEmailNotice && isAADOrganizationEmail && (
              <div className={styles.notice} role="status" aria-live="polite">
                <Icon
                  aria-hidden="true"
                  className={styles.noticeIcon}
                  icon={InfoSolid}
                  iconSize={IconSize.Custom}
                />
                <span className={styles.noticeContent}>
                  <strong>{organizationEmailNoticeTitle}</strong>
                  <span>{organizationEmailNoticeDescription}</span>
                </span>
              </div>
            )}
            <label className={styles.field}>
              <span>{jobTitleOptionalLabel}</span>
              <input
                disabled={isBusy || areContactFieldsReadOnly}
                maxLength={50}
                type="text"
                value={jobTitle}
                onChange={event => {
                  setJobTitle(event.target.value);
                  setError(null);
                }}
              />
            </label>
          </div>
        </section>
        {roleContent ? (
          <section className={styles.section}>
            <h3 className={styles.sectionTitle}>{assignedRoleTitle}</h3>
            <div className={styles.roleOptions}>{roleContent}</div>
          </section>
        ) : roleMode === "select" ? (
          <fieldset className={styles.section}>
            <legend className={styles.sectionTitle}>{selectRoleTitle}</legend>
            <div className={styles.roleOptions} role="radiogroup" aria-label={accessLevelLabel}>
              {visibleRoles.map(option => (
                <label
                  key={option.id}
                  className={`${styles.roleOption} ${
                    role === option.accessLevel ? styles.roleOptionSelected : ""
                  }`}
                >
                  <InvitationRole option={option} hubAccessLabel={hubAccessLabel} />
                  <input
                    type="radio"
                    name="partner-admin-role"
                    value={option.id}
                    checked={role === option.accessLevel}
                    disabled={isBusy}
                    onChange={() => setRole(option.accessLevel)}
                  />
                </label>
              ))}
            </div>
          </fieldset>
        ) : roleMode === "assigned" ? (
          <section className={styles.section}>
            <h3 className={styles.sectionTitle}>{assignedRoleTitle}</h3>
            <div className={styles.roleOptions}>
              {visibleRoles.map(option => (
                <div
                  key={option.id}
                  className={`${styles.roleOption} ${styles.roleOptionSelected} ${styles.assignedRole}`}
                >
                  <InvitationRole option={option} hubAccessLabel={hubAccessLabel} />
                  {assignedRoleActionLabel && onSecondaryAction && (
                    <button type="button" className={styles.roleAction} onClick={onSecondaryAction}>
                      {assignedRoleActionLabel}
                    </button>
                  )}
                </div>
              ))}
            </div>
          </section>
        ) : null}
        {children}
        {error &&
          (error === submitError ? (
            <HubNotification
              actionLabel={tryAgainLabel}
              closeLabel={closeNotificationLabel}
              description={submitError}
              kind="error"
              onAction={() => formRef.current?.requestSubmit()}
              onClose={() => setError(null)}
              title={sendErrorTitle}
            />
          ) : (
            <p className={styles.error} role="alert">
              {error}
            </p>
          ))}
      </div>
      <div className={styles.actions}>
        {dangerActionLabel && onDangerAction && (
          <button type="button" className={styles.error} onClick={onDangerAction}>
            <Icon aria-hidden="true" icon={Delete} iconSize={IconSize.XSmall} />
            {dangerActionLabel}
          </button>
        )}
        {!hideCancel && (
          <BasicButton
            as="button"
            buttonType={ButtonType.Secondary}
            type="button"
            disabled={isBusy}
            onClick={onCancel}
          >
            {cancelLabel}
          </BasicButton>
        )}
        {secondaryActionLabel && onSecondaryAction && (
          <BasicButton
            as="button"
            buttonType={ButtonType.Secondary}
            type="button"
            disabled={isBusy}
            onClick={onSecondaryAction}
          >
            {secondaryActionLabel}
          </BasicButton>
        )}
        {!hideSubmit && (
          <BasicButton as="button" buttonType={ButtonType.Brand} type="submit" disabled={isBusy}>
            {submitLabel}
          </BasicButton>
        )}
      </div>
      {isSubmitting && (
        <div className={styles.submittingOverlay} role="status" aria-label={submittingLabel}>
          <LoadingSpinner />
        </div>
      )}
    </form>
  );
};
